import { X } from '@phosphor-icons/react';
import { DEFAULT_THREAD_TITLE, SVG_SIZE_M } from 'src/constants';
import {
  useGetEditorThread,
  useEditorState,
  useQueryParams,
  useRightSidePanelActions,
  useRightSidePanelState,
  useVisible,
} from 'src/hooks';
import { Button } from 'src/v2/commonComponents/Button';
import { DownloadFileTool } from '../DownloadFileTool';
import styles from './EditorHeader.module.scss';
import { FullWidthToggle } from '../FullWidthToggle';
import { ArtifactType } from 'src/types';
import { CopyCodeButton } from 'src/v2/components/CopyCodeButton';
import { VersioningControl } from '../VersioningControl';
import { EditorMenuControl } from '../EditorMenuControl';
import { BetaLabel } from 'src/v2/commonComponents/BetaLabel';
import { EditorEditableTitle } from '../EditorEditableTitle';
import { EditorAutoSaveLabel } from '../EditorAutoSaveLabel';
import { DownloadEditorPodcastButton } from '../DownloadEditorPodcastButton';
import { useChatHistory } from 'src/hooks';

export const EditorHeader = () => {
  const { isNinjaRetired } = useChatHistory();
  const { removeSearchParam } = useQueryParams();

  const { editorType, codeEditorData } = useEditorState();
  const { isRightPanelFullWidth } = useRightSidePanelState();
  const { toggleRightSidePanelExpanded, toggleRightSidePanelFullWidth } =
    useRightSidePanelActions();
  const { editorThread } = useGetEditorThread();
  const { thread_id, thread_title } = editorThread || {};
  const editorTitle = thread_title || DEFAULT_THREAD_TITLE;

  const {
    isVisible: isBetaLabelVisible,
    onVisibilityRemove: onBetaLabelClose,
    onVisibilitySet: onBetaLabelOpen,
  } = useVisible(true);

  const handleClose = () => {
    removeSearchParam('editor');
    toggleRightSidePanelFullWidth(false);
    toggleRightSidePanelExpanded();
  };

  const handleTitleEditingChange = (isEditing: boolean) => {
    if (isEditing) {
      onBetaLabelClose();
    } else {
      onBetaLabelOpen();
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.wrapper}>
        <EditorAutoSaveLabel />

        <EditorEditableTitle
          title={editorTitle}
          onEditChange={handleTitleEditingChange}
        />

        {!isNinjaRetired && isBetaLabelVisible && <BetaLabel />}
      </div>

      <div className={styles.wrapper}>
        {!isNinjaRetired && <FullWidthToggle />}

        {!isNinjaRetired && <VersioningControl />}

        {!isNinjaRetired && editorType === ArtifactType.TEXT && (
          <DownloadFileTool
            toolType="editor"
            taskTitle={thread_title?.replaceAll(' ', '_') || ''}
          />
        )}

        {editorType === ArtifactType.CODE && codeEditorData?.content && (
          <CopyCodeButton content={codeEditorData.content} />
        )}

        {!isNinjaRetired && <DownloadEditorPodcastButton />}

        {thread_id && (
          <EditorMenuControl
            threadId={thread_id}
            threadTitle={thread_title}
            placement="header"
          />
        )}

        {!isRightPanelFullWidth && (
          <Button
            shape="round"
            color="transparent"
            className={styles.closeButton}
            onClick={handleClose}
          >
            <X size={SVG_SIZE_M} />
          </Button>
        )}
      </div>
    </div>
  );
};
